import { ElementRef, InjectionToken, Injector, StaticProvider } from '@angular/core';

/**
 * AppModal — the in-house replacement for the (vendored + npm) NgbModal on V2
 * screens. Types only; the opener is `AppModalService` (app-modal.service.ts)
 * and the chrome is `AppModalComponent` (app-modal.component.ts).
 *
 * Why it exists (owner-reported 2026-07-15): the legacy NgbModal services are
 * ROOT singletons, so their modal content is created against the ROOT
 * injector — every screen/route-scoped service a content component injects
 * (the MarriageComponent/MultipleDevoteeModalService case) throws
 * NullInjectorError and the modal silently never opens on V2 pages. AppModal
 * takes the CALLER's `injector` so content resolves exactly what the opening
 * screen can resolve, and it bridges both `NgbActiveModal` classes so legacy
 * modal components can be hosted UNCHANGED during the migration.
 */

export type AppModalSize = 'sm' | 'md' | 'lg' | 'xl';

/** One footer-bar button. The bar is fully caller-defined (order included). */
export interface AppModalButton {
    label: string;
    /** Visual style; default 'primary'. */
    kind?: 'primary' | 'success' | 'danger' | 'secondary' | 'outline';
    /**
     * 'close' (default) resolves `result` with `value`;
     * 'dismiss' rejects it (the cancel path, NgbModal semantics).
     */
    role?: 'close' | 'dismiss';
    /** Result value for role 'close'. */
    value?: unknown;
    /**
     * Runs the CONTENT's own logic instead of the default close/dismiss, for
     * footer buttons whose outcome the shell cannot know: a hosted form's
     * Submit computes the result and closes through its (bridged) activeModal —
     * a fixed `value` could never carry it. The handler owns the close policy,
     * so a failed validation can simply leave the card open.
     */
    action?: (ref: AppModalRef) => void;
    /**
     * Keyboard shortcut bound to this button: 'enter' activates it on the Enter
     * key, 'esc' on Escape (the standard dismiss), 'f1' on F1 (the legacy
     * booking-modal Submit key). Renders a `<kbd>` chip on the button and a
     * hover tooltip. Opt-in — only buttons that set it are bound, so hosted
     * forms keep their own key handling untouched.
     */
    hotkey?: 'enter' | 'esc' | 'f1';
    /** Tooltip override; defaults to "Click ENTER to confirm" / "Press ESC to close". */
    hotkeyTip?: string;
}

/** Standard Save/Cancel bar (Save resolves `true`). */
export const APP_MODAL_SAVE_CANCEL: readonly AppModalButton[] = [
    { label: 'Cancel', kind: 'outline', role: 'dismiss' },
    { label: 'Save', kind: 'primary', role: 'close', value: true },
];

/**
 * The 'Confirmation Dialog' classification's button bar: Close (ESC, dismisses
 * → resolves false) + Confirm (ENTER, primary → resolves true). Used by
 * `AppModalService.confirmDialog`. Both carry keyboard chips + tooltips.
 */
export const APP_MODAL_CONFIRM_CANCEL: readonly AppModalButton[] = [
    { label: 'Close', kind: 'outline', role: 'dismiss', hotkey: 'esc' },
    { label: 'Confirm', kind: 'primary', role: 'close', value: true, hotkey: 'enter' },
];

/** Destructive-action bar: red Delete (resolves `true`) + Cancel. */
export function appModalDeleteButtons(deleteLabel = 'Delete'): AppModalButton[] {
    return [
        { label: 'Cancel', kind: 'outline', role: 'dismiss' },
        { label: deleteLabel, kind: 'danger', role: 'close', value: true },
    ];
}

/**
 * Anchored "confirm popup" placement (the PrimeNG ConfirmPopup shape): instead
 * of a centred card the modal is a small panel pinned to an element, with an
 * arrow pointing at it. Everything else (buttons, hotkeys, result promise)
 * behaves exactly like an ordinary AppModal.
 */
export interface AppModalConfirmPopup {
    /** The element the popup points at (an ElementRef or the node itself). */
    anchor: ElementRef<HTMLElement> | HTMLElement;
    /** Gap between the anchor and the panel, px (default 10 — room for the arrow). */
    offset?: number;
}

export interface AppModalOptions {
    /** Header title. Omit (with showClose false) for a chrome-less card. */
    title?: string;
    /** Card width preset; default 'md'. */
    size?: AppModalSize;
    /** Footer button bar; omit/[] for none (content brings its own). */
    buttons?: readonly AppModalButton[];
    /** Top-right ✕ (dismisses); default true. */
    showClose?: boolean;
    /** Click on the backdrop dismisses; default false (static backdrop). */
    backdropDismiss?: boolean;
    /** Escape dismisses; default true. */
    keyboard?: boolean;
    /**
     * The OPENING screen's `Injector` (`inject(Injector)` in the component).
     * Required whenever the content needs route/screen-scoped services —
     * without it content resolves against the root injector only.
     */
    injector?: Injector;
    /** Extra providers for the content's element injector. */
    providers?: StaticProvider[];
    /** Arbitrary payload; content components read it via APP_MODAL_DATA. */
    data?: unknown;
    /** Extra CSS class(es) on the card. */
    modalClass?: string;
    /**
     * Render as an anchored popup with an arrow instead of a centred card.
     * The card keeps its normal chrome/buttons; only the placement changes.
     */
    confirmPopup?: AppModalConfirmPopup;
}

/** Inject in content components to read `AppModalOptions.data`. */
export const APP_MODAL_DATA = new InjectionToken<unknown>('APP_MODAL_DATA');

/**
 * Handle for one open modal. `result` follows NgbModal semantics: resolves on
 * `close(value)`, rejects on `dismiss(reason)` / ✕ / Esc / backdrop.
 */
export class AppModalRef<T = unknown> {
    /** Instance of the content component (undefined for template content). */
    componentInstance?: T;
    readonly result: Promise<unknown>;

    private resolveFn!: (value: unknown) => void;
    private rejectFn!: (reason?: unknown) => void;
    private settled = false;
    private teardown: () => void = () => { };

    constructor() {
        this.result = new Promise((resolve, reject) => {
            this.resolveFn = resolve;
            this.rejectFn = reject;
        });
        // A dismissal nobody observes must not surface as an unhandled
        // rejection (parity with how call sites treat NgbModal refs).
        this.result.catch(() => { });
    }

    /** Wires the overlay teardown; called once by AppModalService. */
    bindTeardown(teardown: () => void): void {
        this.teardown = teardown;
    }

    close(value?: unknown): void {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this.resolveFn(value);
        this.teardown();
    }

    dismiss(reason?: unknown): void {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this.rejectFn(reason);
        this.teardown();
    }
}
